import type {
  PersistedClient,
  Persister,
} from "@tanstack/query-persist-client-core";
import {
  defaultShouldDehydrateQuery,
  type Query,
} from "@tanstack/react-query";
import { getQueryCache, putQueryCache, deleteQueryCache } from "./db";
import { isPinnedSync } from "./pins";

export type PersisterErrorPhase = "persist" | "restore" | "remove";

export interface CreateIDBPersisterOptions {
  /**
   * Called once when the persister disables itself after a storage failure.
   * Used by `main.tsx` to surface IDB / quota errors to Sentry so we know
   * how often offline persistence is failing in production.
   */
  onError?: (err: unknown, phase: PersisterErrorPhase) => void;
}

/**
 * IDB-backed persister for TanStack Query. The persister payload is a single
 * row in the `queryClientCache` object store keyed by "cache". One row keeps
 * writes atomic and avoids fragmentation across the cache lifetime.
 *
 * Storage failures (blocked IDB, quota exceeded, private-browsing quirks)
 * disable the persister for the rest of the session and surface to `onError`
 * once. The app continues running online-only; future persist / restore
 * calls become no-ops.
 */
export function createIDBPersister(
  options: CreateIDBPersisterOptions = {},
): Persister {
  let disabled = false;
  const disable = (err: unknown, phase: PersisterErrorPhase) => {
    if (disabled) return;
    disabled = true;
    options.onError?.(err, phase);
  };

  return {
    async persistClient(client: PersistedClient) {
      if (disabled) return;
      try {
        await putQueryCache(client);
      } catch (err) {
        disable(err, "persist");
      }
    },
    async restoreClient() {
      if (disabled) return undefined;
      try {
        return (await getQueryCache()) as PersistedClient | undefined;
      } catch (err) {
        disable(err, "restore");
        return undefined;
      }
    },
    async removeClient() {
      if (disabled) return;
      try {
        await deleteQueryCache();
      } catch (err) {
        disable(err, "remove");
      }
    },
  };
}

/**
 * Decide whether a query should be written to the persister snapshot.
 *
 * Composes with TanStack's `defaultShouldDehydrateQuery` so that non-success
 * queries (pending / error) are skipped — this is critical because pending
 * queries carry a `query.promise` that the structured-clone algorithm used
 * by IndexedDB can't serialize, throwing `DataCloneError`. Reproducible
 * during normal app startup before `["user","me"]` / `["projects","list"]`
 * have settled, so missing this check would corrupt every cold-boot write.
 *
 * On top of that:
 * - `["user", "me"]` and `["projects", "list"]` are persisted (no pin gate):
 *   the cached identity and project picker are needed offline regardless of
 *   which projects are pinned.
 * - `["projects", "detail", id]`, `["records", id, ...]`, `["plans", id, "list"]`,
 *   `["record-creators", id]` are persisted only when `id` is currently pinned.
 *   Pin status comes from the synchronous in-memory cache populated by
 *   `initializePins`.
 * - Everything else (mutations, ephemeral lookups) is excluded.
 */
export function shouldDehydrateQuery(query: Query): boolean {
  if (!defaultShouldDehydrateQuery(query)) return false;

  const key = query.queryKey;
  if (!Array.isArray(key) || key.length === 0) return false;

  const [root, ...rest] = key;

  if (root === "user" && rest[0] === "me") return true;
  if (root === "projects" && rest[0] === "list") return true;

  if (root === "projects" && rest[0] === "detail") {
    const id = rest[1];
    return typeof id === "string" && isPinnedSync(id);
  }

  if (root === "records") {
    const id = rest[0];
    return typeof id === "string" && isPinnedSync(id);
  }

  if (root === "plans") {
    const id = rest[0];
    return typeof id === "string" && rest[1] === "list" && isPinnedSync(id);
  }

  if (root === "record-creators") {
    const id = rest[0];
    return typeof id === "string" && isPinnedSync(id);
  }

  return false;
}
